/**
 * DocumentTypeMultiSelect — Etap 3, blok D.
 *
 * Multi-select nazw typów dokumentów (zamiast wklejania surowych UUID) dla filtra
 * stałego `document_type_id in […]` na pozycji menu (`BaseFiltersEditor`). Zawężenie
 * ma sens wyłącznie dla klas `specjalne`/`internal` (jedyne listy konsumujące
 * `documentTypeIds` — bloki B/C), więc caller (`BaseFiltersEditor`) renderuje ten
 * komponent tylko dla tych dwóch klas.
 *
 * scope='ite': pobiera OPUBLIKOWANE typy globalne danej klasy z katalogu ITE
 * (`GET /api/admin/global-document-types?class=<cls>&status=published`).
 *
 * scope='tenant': scalona lista typów widocznych dla bieżącego usera (własne +
 * nieukryte globalne) przez hook `useDocumentTypes` (`GET /api/document-types?class=`).
 * Kreator menu tenanta jest „na cały tenant" (bez wyboru firmy), a typy globalne są
 * materializowane per firma — więc deduplikujemy po `id` (ten sam typ globalny powtarza
 * się między firmami). User przypisany tylko do części firm zobaczy podzbiór typów
 * (świadome ograniczenie — brak kontekstu firmy w kreatorze menu).
 *
 * Każdy scope ma własny pod-komponent, żeby jego hook danych wołał się bezwarunkowo
 * (reguła hooks) — nie ma jednego komponentu wołającego oba źródła naraz.
 */

import { useTranslation } from 'react-i18next';
import type { AdminGlobalDocumentType, DocumentClass } from '@saas/shared';
import { useQuery } from '@tanstack/react-query';

import { MultiSelect } from '@/components/MultiSelect';
import { api } from '@/lib/api';
import { useDocumentTypes } from '@/lib/use-document-types';

export interface DocumentTypeMultiSelectProps {
  /** Wybrane id typów (controlled). */
  value: string[];
  /** Callback przy zmianie selekcji. */
  onChange: (ids: string[]) => void;
  /** Klasa dokumentu — zakres zapytania o typy. */
  documentClass: DocumentClass;
  /** Źródło danych: katalog ITE (opublikowane typy globalne) albo tenant (scalona lista). */
  scope: 'ite' | 'tenant';
  disabled?: boolean;
}

/** Znormalizowany typ dokumentu do opcji pickera — wspólny kształt dla obu scope'ów. */
interface PickerType {
  id: string;
  name: string;
  code: string | null;
}

/** Wspólne ciało: stany ładowania/błędu/pustki + `MultiSelect`. */
function PickerBody({
  value,
  onChange,
  disabled,
  types,
  isLoading,
  isError,
}: {
  value: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  types: PickerType[];
  isLoading: boolean;
  isError: boolean;
}) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <p className="py-1.5 text-xs text-text-muted" data-testid="document-type-multiselect-loading">
        {t('admin.menuItemBaseFilters.documentTypePickerLoading', {
          defaultValue: 'Ładuję typy dokumentów…',
        })}
      </p>
    );
  }

  if (isError) {
    return (
      <p
        className="py-1.5 text-xs text-[rgb(var(--color-error-text))]"
        data-testid="document-type-multiselect-error"
      >
        {t('admin.menuItemBaseFilters.documentTypePickerError', {
          defaultValue: 'Nie udało się wczytać typów dokumentów.',
        })}
      </p>
    );
  }

  if (types.length === 0) {
    return (
      <p className="py-1.5 text-xs text-text-muted" data-testid="document-type-multiselect-empty">
        {t('admin.menuItemBaseFilters.documentTypePickerEmpty', {
          defaultValue: 'Brak typów dokumentów dla tej klasy.',
        })}
      </p>
    );
  }

  const options = types.map((type) => ({
    value: type.id,
    label: type.name,
    description: type.code || undefined,
  }));

  return (
    <MultiSelect
      value={value}
      onChange={onChange}
      options={options}
      disabled={disabled}
      placeholder={t('admin.menuItemBaseFilters.documentTypePickerPlaceholder', {
        defaultValue: 'Wybierz typy dokumentów…',
      })}
      data-testid="document-type-multiselect"
    />
  );
}

/** scope='ite' — opublikowane typy globalne z katalogu ITE. */
function ItePicker({
  value,
  onChange,
  documentClass,
  disabled,
}: Omit<DocumentTypeMultiSelectProps, 'scope'>) {
  const query = useQuery({
    queryKey: ['admin', 'global-document-types', documentClass, 'published'],
    queryFn: async () =>
      (
        await api.get<{ types: AdminGlobalDocumentType[] }>(
          `/admin/global-document-types?class=${documentClass}&status=published`,
        )
      ).data,
    retry: false,
  });

  const types: PickerType[] = (query.data?.types ?? []).map((type) => ({
    id: type.id,
    name: type.name,
    code: type.code ?? null,
  }));

  return (
    <PickerBody
      value={value}
      onChange={onChange}
      disabled={disabled}
      types={types}
      isLoading={query.isLoading}
      isError={query.isError}
    />
  );
}

/** scope='tenant' — scalone typy widoczne dla usera, deduplikowane po id. */
function TenantPicker({
  value,
  onChange,
  documentClass,
  disabled,
}: Omit<DocumentTypeMultiSelectProps, 'scope'>) {
  const { data, isLoading, isError } = useDocumentTypes(documentClass);

  // Typ globalny materializuje się per firma → to samo `id` powtarza się w scalonej
  // liście. Deduplikujemy, żeby picker nie pokazał tego samego typu wiele razy.
  const seen = new Set<string>();
  const types: PickerType[] = [];
  for (const row of data) {
    if (seen.has(row.id)) continue;
    seen.add(row.id);
    types.push({ id: row.id, name: row.name, code: row.code ?? null });
  }

  return (
    <PickerBody
      value={value}
      onChange={onChange}
      disabled={disabled}
      types={types}
      isLoading={isLoading}
      isError={isError}
    />
  );
}

export function DocumentTypeMultiSelect({
  value,
  onChange,
  documentClass,
  scope,
  disabled,
}: DocumentTypeMultiSelectProps) {
  const inner = { value, onChange, documentClass, disabled };
  return scope === 'tenant' ? <TenantPicker {...inner} /> : <ItePicker {...inner} />;
}
